import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AnimatedNumber from "animated-number-react";
import { ProgressBar } from "react-step-progress-bar";
import FlipMove from "react-flip-move";
import EmptyBanner from "../../../components/EmptyBanner";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useAuth } from "../../../providers/AuthProvider";
import { useOpportunity } from "../../../providers/OpportunityProvider";
import NEWSkillFormButton from '../../profiles/components/NEWSkillFormButton';

const TalentMetricFormSection = ({ user, header, propName, userPropName, ...props }) => {

    const initItems = (items = []) => {
        if (!user) {
            return [];
        }

        if (items && !TB.isArray(items)) {
            items = [items];
        }

        let userSelectedItems = TB.keyBy(
            user[userPropName] || [], 'id'
        )

        items = TB.map(items, item => {
            if (userSelectedItems[item.id]) {
                return {
                    ...item,
                    ...userSelectedItems[item.id]
                };
            }
            return item;
        })

        return TB.orderBy(items, item =>
            TB.isDefined(item.self_score) ? 0 : 1, 'asc'
        );
    }

    const [formItems, setFormItems] = useState(initItems(props.job[propName]));

    return (
        <div className="form-section">
            <h5>
                {header}
            </h5>
            <div className="discipline-skills-container skill-importance-container gap-3">
                {formItems?.length > 0 ? (
                    <FlipMove typeName={null}>
                        {TB.map(formItems, (item, index) => (
                            <div key={`${propName}-${item.id}-${item.self_score}-${index}`}>
                                <NEWSkillFormButton
                                    skill={item}
                                    disabled={props.disabled}
                                    disableRemove
                                    scoringOptions={item.category ? laravel.talent_scoring_options_by_category[item.category] : laravel.talent_scoring_options}
                                    type={item.category ? 'info' : 'default'}
                                    noMatch={TB.isNil(item.self_score)}
                                    onChange={async (model) => {
                                        if (!props.job.is_match) {
                                            return TB.toast('This person doesn\'t have the required role', { theme: 'danger' })
                                        }

                                        if (item.self_score && item.self_score === model.self_score) {
                                            return;
                                        }
                                        let updatedFormItems = [...formItems];

                                        updatedFormItems[index] = { ...model };

                                        updatedFormItems = TB.orderBy(updatedFormItems, item =>
                                            TB.isDefined(item.self_score) ? 0 : 1, 'asc'
                                        );

                                        await setFormItems(updatedFormItems);

                                        let request = await props.onUpdateItem({ [propName]: [{ ...model }] })

                                        if (!request) {
                                            await setFormItems(initItems(props.job[propName]))
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </FlipMove>
                ) : (
                    <EmptyBanner message={`No Required ${TB.capitalize(propName)} Listed`} />
                )}
            </div>

        </div>
    )
}

const TalentJobMatchForm = props => {

    const metrics = props.job.metricScores();
    const job = props.job;
    const [disabled, setDisabled] = useState(props.disabled);
    const { auth } = useAuth();

    const {
        opportunity,
        onRequest,
        changeProps
    } = useOpportunity();
    if (!auth.canManageClientResourceTalent()) {
        return null
    }

    const onUpdateItem = async (updates) => {

        await setDisabled(true)
        try {
            let updatedOpp = await props.onRequest(async () => {
                return opportunity.updateMatchingForTalent(props.job, props.user, updates);
            })
            if (!updatedOpp) {
                await TB.showServerErrorToast();
                await setDisabled(false)
                return false
            }
            await onRequest(() => changeProps(updatedOpp))

            await setDisabled(false)
            await TB.sysAlert('talent.update_job_matching_success');

            return true;
        } catch (e) {
            console.warn(e)
            await setDisabled(false)
            return false
        }

    }

    const renderMetricProgress = (metric) => {
        let { overall } = metric;
        let disabled = TB.isDefined(overall?.max_count) && overall.max_count === 0;

        let metricClass = "job-metric-match";
        if (disabled) metricClass += " disabled"
        return (
            <div className={metricClass}>
                {metric.renderProgress()}
            </div>
        )
    }

    const isDisabled = (props.disabled || disabled);

    if (props.minimal) {
        return (
            <div className={TB.mapClass("job-match-scores-container row minimal", {
                disabled: isDisabled
            })}>

                {(isDisabled) && (
                    <div className="match-updating">
                        <LoadingSpinner size={'lg'} />
                    </div>
                )}

                <div className="job-match-header">
                    <div className="score-progress">
                        <p className="overall-progress">
                            <AnimatedNumber
                                value={TB.round(job.matching.overall.percent, 2)}
                                formatValue={(value) => `${TB.round(parseInt(value), 2)}% Total Match`}
                                duration={1000} />
                        </p>

                        <ProgressBar
                            filledBackground={TB.config('color.orange')}
                            height={20}
                            percent={TB.round(job.matching.overall.percent, 2)}
                            unfilledBackground={TB.config('color.gray')} />
                    </div>
                </div>
                <div className="job-match-scores col col-auto">
                    <h4>Skill Requirements</h4>
                    <div className="match-section">
                        <div className="metric-job-scores">
                            {renderMetricProgress(TB.find(metrics, ['type', 'skills']))}
                        </div>
                        <TalentMetricFormSection
                            {...props}
                            propName="skills"
                            header="Skill Requirements"
                            userPropName="skills"
                            propIndex={1}
                            disabled={isDisabled}
                            onUpdateItem={onUpdateItem}
                        />
                    </div>
                    <h4>Total Experience</h4>

                    <div className="match-section">
                        <div className="metric-job-scores">
                            {renderMetricProgress(TB.find(metrics, ['type', 'experience']))}
                        </div>
                        <TalentMetricFormSection
                            {...props}
                            propIndex={2}
                            propName="disciplines"
                            header="Total Experience"
                            userPropName="talent_disciplines"
                            valuePropName={undefined}
                            disabled={isDisabled}
                            onUpdateItem={onUpdateItem}
                        />
                    </div>

                    <h4>Industry Requirements</h4>
                    <div className="match-section">
                        <div className="metric-job-scores">
                            {renderMetricProgress(TB.find(metrics, ['type', 'industries']))}
                        </div>

                        <TalentMetricFormSection
                            {...props}
                            propIndex={3}
                            propName="industries"
                            header="Industry Requirements"
                            userPropName="industries_worked"
                            disabled={isDisabled}
                            onUpdateItem={onUpdateItem}
                        />
                    </div>
                </div>
            </div>
        )
    }
    return (
        <React.Fragment>
            <div className={TB.mapClass("job-match-scores-container row m-0", {
                disabled: isDisabled
            })}>

                {isDisabled && (
                    <div className="match-updating">
                        <LoadingSpinner size={'lg'} />
                    </div>
                )}
                <div className="job-match-scores col col-auto">
                    <div className="score-progress">
                        <p className="overall-progress">
                            <AnimatedNumber
                                value={TB.round(job.matching.overall.percent, 2)}
                                formatValue={(value) => `${TB.round(parseInt(value), 2)}% Total Match`}
                                duration={1000} />
                        </p>

                        <ProgressBar
                            filledBackground={TB.config('color.orange')}
                            height={20}
                            percent={TB.round(job.matching.overall.percent, 2)}
                            unfilledBackground={TB.config('color.gray')} />
                    </div>

                    <div className="metric-job-scores">
                        {TB.orderBy(metrics, metric => {
                            switch (metric.type) {
                                case 'skills':
                                    return 1;
                                case 'industries':
                                    return 2;
                                case 'experience':
                                    return 3;
                            }
                        }).map((metric, i) => {

                            let tooltipId = `talent-${job.id}-${i}`;

                            let { overall } = metric;
                            let disabled = TB.isDefined(overall?.max_count) && overall.max_count === 0;

                            let metricClass = "job-metric-match";
                            if (disabled) metricClass += " disabled"
                            return (
                                <div key={tooltipId} className={metricClass}>
                                    <p>{metric.label}</p>

                                    {metric.renderProgress()}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="job-match-form col col-auto">
                    <div className="form-section-container">
                        <TalentMetricFormSection
                            {...props}
                            propName={'skills'}
                            header="Skill Requirements"
                            userPropName={'skills'}
                            disabled={isDisabled}
                            propIndex={1}
                            onUpdateItem={onUpdateItem}
                        />
                        <TalentMetricFormSection
                            {...props}
                            disabled={isDisabled}
                            propIndex={2}
                            propName={'disciplines'}
                            header="Total Experience"
                            userPropName={'talent_disciplines'}
                            valuePropName={undefined}
                            onUpdateItem={onUpdateItem}
                        />
                        <TalentMetricFormSection
                            {...props}
                            propIndex={3}
                            propName={'industries'}
                            header="Industry Requirements"
                            disabled={isDisabled}
                            userPropName={'industries_worked'}
                            onUpdateItem={onUpdateItem}
                        />
                    </div>
                </div>

                <div className="col col-auto hidden lg:flex items-center">
                    <i className="fas fa-arrow-left pr-3" style={{ fontSize: 30 }} />
                    <p>
                        Please review the client’s requirements compared to what’s in the talent's profile. If they have
                        experience in missing areas (in gray), you can add them to thier profile here.
                    </p>
                </div>
            </div>

            <div className="col col-auto lg:hidden items-center p-3">
                <i className="fas fa-arrow-up pb-3" style={{ fontSize: 40, left: '68%', position: 'relative' }} />
                <p className="px-2">Please review the client’s requirements compared to what’s in the talent's profile. If
                    they have experience in missing areas (in gray), you can add them to their profile here.</p>
            </div>

        </React.Fragment>
    )

};

TalentJobMatchForm.propTypes = {
    job: PropTypes.object,
    onUpdateMatching: PropTypes.func,
    minimal: PropTypes.bool, // removes extra unneeded details from component
};

export default TalentJobMatchForm;